import React, { useState, useEffect } from "react";
import { X, User, Phone, Briefcase, FileText, CreditCard } from "lucide-react";
import { toast } from '../../utils/toast';
import { EmployeeForm } from "../common/types";
import { PersonalInfoTab } from "./PersonalInfoTab";
import { ContactInfoTab } from "./ContactInfoTab";
import { WorkInfoTab } from "./WorkInfoTab";
import { OtherInfoTab } from "./OtherInfoTab";
import { AccountInfoTab } from "./AccountInfoTab";
import * as Yup from "yup";
import { useFormik } from "formik";
import { addNewEmployee, editEmployee } from "../../api/employee";
import { useAuth } from "../../context/AuthContext";

interface EmployeeFormModalProps {
  isOpen: boolean;
  isEdit: boolean;
  onOpenChange: (open: boolean) => void;
  employee?: EmployeeForm | null;
  onSuccess: () => void;
  title: string;
  submitButtonText: string;
}

// Validation schemas
const personalSchema = Yup.object({
  name: Yup.string().required("Name required"),
  gender: Yup.string().required("Gender Required"),
  work_type: Yup.string().required("Work Type Required"),
});

const contactSchema = Yup.object({
  email: Yup.string().required("Email Required"),
  mobile: Yup.string().required("Primary Contact Required"),
  zone_id: Yup.string().required("Zone Required"),
  head_quarters: Yup.array().min(1, "Select at least one Head Quarter"),
});

const workSchema = Yup.object({
  designation_id: Yup.number().required("Designation required").min(1, "Designation required"),
  doj: Yup.string().required("Date of Joining required"),
});
const otherSchema = Yup.object({});
const accountSchema = Yup.object({});

const EmployeeFormModal: React.FC<EmployeeFormModalProps> = ({
  isOpen,
  onOpenChange,
  employee,
  onSuccess,
  title,
  submitButtonText,
  isEdit
}) => {
  const [step, setStep] = useState(0);
  const [activeTab, setActiveTab] = useState("personal");
  const { user } = useAuth();

  const validationSchemas = [
    personalSchema,
    contactSchema,
    workSchema,
    otherSchema,
    accountSchema,
  ];

  const EMPTY_INITIAL_VALUES: EmployeeForm = {
    id: 0,
    tenant_id: user?.tenant_id ?? 0,
    user_id: 0,
    country_id: 0,
    zone_id: 0,
    head_quarter_id: 0,
    head_quarters: [],
    assigned_locations: [],
    out_station_hqs: [],
    designation_id: 0,
    ex_stations: [],
    out_stations: [],
    // accompanied_employee: [],
    name: "",
    email: "",
    code: "",
    photo: "",
    gender: "",
    work_type: "",
    marital_status: "",
    anniversary: "",
    mobile: "",
    alternate_mobile: "",
    current_address: "",
    permanent_address: "",
    zip: "",
    assigned_to: 0,
    additional_supervisor: 0,
    doj: "",
    dob: "",
    qualification: "",
    adhar_no: "",
    pan_no: "",
    uan: "",
    dl_no: "",
    dl_expiry_date: "",
    blood_group: "",
    da_hq: 0,
    da_ex: 0,
    da_out: 0,
    da_rhq: 0,
    da_transit: 0,
    da_other: 0,
    per_km: 0,
    banking_name: "",
    account_number: "",
    ifsc_code: "",
    beneficiary_id: "",
    bank_name: "",
    branch_name: "",
    nominee_name: "",
    annual_income: "",
    created_at: "",
    updated_at: "",
    user: {} as any,
  };

  // Initialize form values based on whether we're editing or creating
  const getInitialValues = () => {
    if (isEdit && employee) {
      return {
        ...employee,
        head_quarters: Array.isArray(employee.head_quarters)
          ? employee.head_quarters
              .map((h: any) => typeof h === 'object' && h !== null ? Number(h.id) : Number(h))
              .filter(Boolean)
          : [],
        assigned_locations: Array.isArray(employee.assigned_locations)
          ? employee.assigned_locations
              .map((l: any) => typeof l === 'object' && l !== null ? Number(l.id) : Number(l))
              .filter(Boolean)
          : [],
        out_station_hqs: Array.isArray(employee.out_station_hqs)
          ? employee.out_station_hqs
              .map((l: any) => typeof l === 'object' && l !== null ? Number(l.id) : Number(l))
              .filter(Boolean)
          : [],
      };
    }
    return EMPTY_INITIAL_VALUES;
  };

  const formik = useFormik<EmployeeForm>({
    initialValues: getInitialValues(),
    enableReinitialize: true,
    validationSchema: validationSchemas[step],
    onSubmit: async (values) => {
      const formData = new FormData();

      Object.entries(values).forEach(([key, value]) => {
        if (value === undefined || value === null) return;

        if (Array.isArray(value)) {
          value.forEach(v => formData.append(`${key}[]`, String(v)));
        } else if (key === "photo" && value instanceof File) {
          formData.append("photo", value);
        } else {
          formData.append(key, String(value));
        }
      });

      try {
        if (isEdit) {
          await editEmployee(values.id, formData);
        } else {
          await addNewEmployee(formData);
        }
        onOpenChange(false);
        onSuccess();
      } catch (error) {
        console.error("Error submitting form:", error);
      }
    },
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      formik.resetForm();
      setActiveTab("personal");
      setStep(0);
    }
  }, [isOpen]);

  // Update form values when employee changes (for edit mode)
  useEffect(() => {
    if (isOpen && isEdit && employee) {
      formik.setValues(employee);
    }
  }, [isOpen, isEdit, employee]);

  if (!isOpen) return null;

  const tabs = [
    { value: 0, id: "personal", label: "Personal Info", icon: User },
    { value: 1, id: "contact", label: "Contact Information", icon: Phone },
    { value: 2, id: "work", label: "Work Information", icon: Briefcase },
    { value: 3, id: "other", label: "Other Information", icon: FileText },
    { value: 4, id: "account", label: "Account Information", icon: CreditCard },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "personal":
        return <PersonalInfoTab formik={formik} currentEmployee={employee} isEdit={isEdit} />;
      case "contact":
        return <ContactInfoTab formik={formik} currentEmployee={employee} />;
      case "work":
        return <WorkInfoTab formik={formik} currentEmployee={employee} />;
      case "other":
        return <OtherInfoTab formik={formik} currentEmployee={employee} />;
      case "account":
        return <AccountInfoTab formik={formik} currentEmployee={employee} />;
      default:
        return <PersonalInfoTab formik={formik} currentEmployee={employee} isEdit={isEdit} />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
          <h2 className="text-2xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={() => onOpenChange(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          <div className="w-80 bg-gray-50 border-r border-gray-200 flex-shrink-0">
            <div className="p-4">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      formik.validateForm().then(errors => {
                        const currentSchema = validationSchemas[step];
                        if (Object.keys(errors).length === 0) {
                          setActiveTab(tab.id);
                          setStep(tab.value);
                        } else {
                          const schemaFields = currentSchema.describe().fields;
                          const currentErrors = Object.keys(errors)
                            .filter((key) => schemaFields[key])
                            .reduce((obj, key) => {
                              obj[key as keyof EmployeeForm] = errors[key as keyof EmployeeForm];
                              return obj;
                            }, {} as Record<string, unknown>);

                          if (Object.keys(currentErrors).length > 0) {
                            toast.warning("Please fill all the required fields in the current tab.");
                          } else {
                            setActiveTab(tab.id);
                            setStep(tab.value);
                          }
                        }
                      });
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors mb-2 ${activeTab === tab.id
                      ? "bg-teal-500 text-white"
                      : "hover:bg-gray-200 text-gray-700"
                      }`}
                  >
                    <Icon className="w-5 h-5" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto">
              <form
                id="employee-form"
                onSubmit={formik.handleSubmit}
                className="p-6"
                encType="multipart/form-data"
              >
                {renderTabContent()}
              </form>
            </div>
            <div className="flex justify-end gap-4 p-6 border-t border-gray-200 flex-shrink-0">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="employee-form"
                className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                {submitButtonText}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeFormModal;